import { TextFieldProps } from '@mui/material';
import { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { useBoolean } from 'usehooks-ts';

import type { PlacePrediction } from '@/types/places';

import { AddressAutocompleteField } from './AddressAutocompleteField';

type AddressInputProps<FormPayload extends FieldValues> = {
  control: Control<FormPayload>;
  name: Path<FormPayload>;
  rules?: Omit<
    RegisterOptions<FormPayload, Path<FormPayload>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  disabled?: boolean;
  placeholder?: string;
  textFieldProps?: TextFieldProps;
};

export function AddressInput<FormPayload extends FieldValues>({
  control,
  rules,
  name,
  disabled,
  placeholder,
  textFieldProps,
}: AddressInputProps<FormPayload>) {
  const {
    value: shouldShowSuggestions,
    setTrue: showSuggestions,
    setFalse: hideSuggestions,
  } = useBoolean(false);
  const { suggestions, setValue: setSearchValue } = usePlacesAutocomplete({
    cache: false,
    debounce: 300,
  });

  const predictions = useMemo(() => suggestions.data || [], [suggestions.data]);

  const predictionById = useMemo(() => {
    const mapper: Record<string, PlacePrediction> = {};
    predictions.forEach(item => {
      mapper[item.place_id] = item;
    });
    return mapper;
  }, [predictions]);

  const placeIds = useMemo(
    () => predictions.map(item => item.place_id),
    [predictions],
  );

  const { inputProps, ...restTextFieldProps } = textFieldProps || {};
  const maxLength = inputProps?.maxLength || undefined;

  return (
    <Controller
      rules={rules}
      control={control}
      name={name}
      render={({ field }) => (
        <AddressAutocompleteField
          dataTestId={`${String(name || 'address')}-input`}
          field={field}
          maxLength={maxLength}
          inputProps={inputProps}
          restTextFieldProps={restTextFieldProps}
          textFieldProps={textFieldProps}
          placeholder={placeholder}
          disabled={disabled}
          shouldShowSuggestions={shouldShowSuggestions}
          showSuggestions={showSuggestions}
          hideSuggestions={hideSuggestions}
          predictionById={predictionById}
          placeIds={placeIds}
          setSearchValue={setSearchValue}
        />
      )}
    />
  );
}
